import Link from "next/link";
import { NothingLayout } from "@/layouts/Nothing";
import { Swiper, SwiperSlide } from "swiper/react";
import Image from "next/image";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  A11y,
  Autoplay,
  Keyboard,
  Navigation,
  Pagination,
  Scrollbar,
} from "swiper";
import React, { useEffect, useRef, useState } from "react";
import { Head } from "@/components/base/Head";
import { useToast } from "@/utils";
import { newOrganizationRepository } from "@/repositories/organization";
import { client } from "@/api/client";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import axios from "axios";
import { emailValidate } from "@/validator/email";
import { SimpleLoading } from "@/components/base/SimpleLoading";

const AboutPage = () => {
  const META_TITLE = "日本初のマンガと動画で経営が学べるオンライン学習サービス";

  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const repository = newOrganizationRepository(client);
  const router = useRouter();

  useEffect(() => {
    if (router.query.referral_code) {
      reset({
        referred_code: router.query.referral_code,
      });
    }
  }, [router]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm();

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      const userData = {
        email: data.email,
        password: data.password,
        password_confirmation: data.password_confirmation,
        referred_code: data.referred_code,
        last_name_ja: data.last_name_ja,
        first_name_ja: data.first_name_ja,
        nickname: data.nickname,
      };
      data.user = userData;
      const response = await repository.createOrganization(data);
      const authHeaders = {
        "access-token": response["access-token"],
        client: response["client"],
        expiry: response["expiry"],
        uid: response["uid"],
      };
      await axios.post("/api/signin", {
        authHeaders: JSON.stringify(authHeaders),
      });
      toast.success("登録が完了しました。");
      router.push("/registrations/confirm");
    } catch (e) {
      if (e === 409) {
        toast.error("このメールアドレスはすでに登録されております。");
      } else {
        toast.error("エラーが発生しました。");
      }
    } finally {
      setLoading(false);
    }
  };

  const password = useRef({});
  password.current = watch("password", "");

  return (
    <>
      <Head title={META_TITLE} canonicalPath={`/about`} />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin={""}
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700;900&display=swap"
        rel="stylesheet"
      />
      <link rel="stylesheet" href="/about/css/style.min.css" />
      <link rel="stylesheet" href="/about/css/style.min.css" />

      <main className={"text-20"}>
        <div className="overflow-hidden">
          <div className="relative mb-o-36">
            <div className="flex items-start justify-center absolute top-0 h-o-1080 w-full">
              <div className="w-1/2 h-o-1080 bg-[#F7F7F7]"></div>
              <div className="w-1/2 h-o-1080 bg-[#EBEEF6]"></div>
              <div className="h-o-765 md:h-o-596 w-full absolute top-0 md:top-o-64 anime-bgmove flex">
                <Image
                  loading={"eager"}
                  src="/about/images/fv_bg.png"
                  alt=""
                  width="1020"
                  height="757"
                  className="w-full block h-o-765 translate-y-[1.4rem] max-w-none anime-display opacity-0 anime-firstdelay md:hidden"
                />
                <Image
                  loading={"eager"}
                  src="/about/images/fv_bg.png"
                  width="1020"
                  height="757"
                  alt=""
                  className="w-full block h-o-765 translate-y-[1.4rem] max-w-none anime-display opacity-0 anime-firstdelay md:hidden"
                />
                <Image
                  loading={"eager"}
                  src="/about/images/fv_bg_pc.png"
                  width="1974"
                  height="509"
                  alt=""
                  className="w-full h-o-765 md:h-o-596 translate-y-[1.4rem] md:-translate-y-[0.2rem] max-w-none anime-display opacity-0 anime-firstdelay hidden md:block"
                />
                <Image
                  loading={"eager"}
                  src="/about/images/fv_bg_pc.png"
                  width="1974"
                  height="509"
                  alt=""
                  className="w-full h-o-765 md:h-o-596 translate-y-[1.4rem] md:-translate-y-[0.2rem] max-w-none anime-display opacity-0 anime-firstdelay hidden md:block"
                />
              </div>
            </div>
            <section className="pt-o-48 md:pt-o-36 relative">
              <Image
                loading={"eager"}
                src="/about/images/fv_img01.png"
                alt="漫画のイメージ"
                width="389"
                height="248"
                className="w-o-360 absolute anime-comic_move01 scale-[2] right-o-100 top-o-200 will-change-[top,right,transform]"
              />
              <Image
                loading={"eager"}
                src="/about/images/fv_img02.png"
                alt="漫画のイメージ"
                width="314"
                height="194"
                className="w-o-310 absolute scale-[2] anime-comic_move02 opacity-0 right-o-100 top-o-200 will-change-[top,right,transform]"
              />
              <h2 className="relative anime-display opacity-0 anime-firstdelay">
                <Image
                  loading={"eager"}
                  src="/about/images/logo.png"
                  alt="中小企業の戦略ラボ"
                  width="352"
                  height="97"
                  className="w-o-234 md:w-o-127 mx-auto mb-o-80 md:mb-o-20"
                />
              </h2>
              <div className="w-o-691 mx-auto md:w-o-1000 relative">
                <div className="block mb-o-24 md:mb-o-8 overflow-hidden anime-word w-0 anime-thirddelay">
                  <div className="bg-white h-o-81 md:h-o-66 inline-block text-center px-o-16">
                    <p className="text-52 md:text-41 font-black tracking-o-60 whitespace-nowrap">
                      「<span className="text-blue">学び</span>」で
                    </p>
                  </div>
                </div>
                <div className="block mb-o-28 overflow-hidden anime-word delay-400 w-0 anime-forthdelay">
                  <div className="bg-white h-o-81 md:h-o-66 inline-block text-center px-o-16">
                    <p className="text-52 md:text-41 font-black tracking-o-60 whitespace-nowrap">
                      会社を強くする
                    </p>
                  </div>
                </div>
                <div className="text-black tracking-o-60 w-o-691 mx-auto font-black leading-o-20 text-23 md:text-17 mb-o-80 md:mb-o-36 md:ml-0 anime-fadein anime-fifthdelay opacity-0">
                  <p>「全員学習・社内実践・学び定着」で自社の”会社力”を</p>
                  <p>底上げできる中小企業のための学習プラットフォーム</p>
                </div>
                <Link
                  href="#form"
                  className="anime-fifthdelay anime-fadein opacity-0 w-o-526 md:w-o-317 btn_color h-o-110 md:h-o-72 flex items-center justify-center relative mx-auto md:ml-0"
                >
                  <span className="text-30 md:text-18 font-bold tracking-o-100">
                    まずは無料体験
                  </span>
                  <span className="block w-o-67 md:w-o-39 absolute right-o-40 md:right-o-24 cta_arrow">
                    <Image
                      loading={"eager"}
                      src="/about/images/cta_arrow.png"
                      width="102"
                      height="49"
                      alt=""
                      className="opacity-0"
                    />
                  </span>
                  <div className="w-o-526 md:w-o-317 absolute h-o-110 md:h-o-72 border-[0.02rem] border-white top-o-8 left-o-8"></div>
                </Link>
              </div>
            </section>
          </div>
          <div className="relative">
            <div className="relative">
              <picture>
                <source
                  srcSet="/about/images/sec01_bg_pc.png"
                  media="(min-width:768px)"
                />
                <Image
                  loading={"eager"}
                  width="1152"
                  height="2192"
                  src="/about/images/sec01_bg.png"
                  alt=""
                  className="w-full h-o-1480 md:h-o-1139 absolute top-0"
                />
              </picture>
            </div>
            <section className="relative">
              <div className="relative">
                <div className="h-o-184 md:h-o-216 w-[23.39rem] md:w-[20.85rem] absolute top-o-52 md:top-o-64 autoplay-slider">
                  <Image
                    loading={"eager"}
                    width="2339"
                    height="216"
                    src="/about/images/sec01_book.png"
                    alt=""
                    className="max-w-none h-o-216 object-cover block w-full md:hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="2339"
                    height="216"
                    src="/about/images/sec01_book.png"
                    alt=""
                    className="max-w-none h-o-216 object-cover block w-full md:hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="2085"
                    height="184"
                    src="/about/images/sec01_book_pc.png"
                    alt=""
                    className="max-w-none h-o-184 object-cover md:block w-full hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="2085"
                    height="184"
                    src="/about/images/sec01_book_pc.png"
                    alt=""
                    className="max-w-none h-o-184 object-cover md:block w-full hidden"
                  />
                </div>
              </div>
              <div className="pt-o-360 md:pt-o-300">
                <p className="text-center text-navy bg-white py-o-8 w-o-171 mx-auto tracking-o-60 font-bold mb-o-16">
                  戦略ラボなら
                </p>
                <div className="text-center font-bold text-white tracking-o-60 mb-o-40">
                  <h2 className="text-46 md:text-31 mb-o-32 md:mb-o-12">
                    自社の経営課題を「学び」で
                    <br className="md:hidden" />
                    解決できる！
                  </h2>
                  <p className="text-28 md:text-23">
                    経営者学習や従業員学習だけでなく、
                    <br className="md:hidden" />
                    <span className="bg-white text-black">
                      実践や定着までワンストップで実現
                    </span>
                    <br />
                    自社の経営課題を自力で解決するための
                    <br className="md:hidden" />
                    ”会社力”を高めることが可能です
                  </p>
                </div>
                <Image
                  loading={"eager"}
                  width="1073"
                  height="1050"
                  src="/about/images/sec01_map.png"
                  alt="中小企業の戦略ラボ"
                  className="w-o-691 md:w-o-527 mx-auto"
                />
              </div>
            </section>
          </div>
          <section className="pt-o-180 pb-o-100 bg-gray">
            <div className="text-center font-bold tracking-o-60 mb-o-44 md:mb-o-60">
              <h2 className="text-31 md:text-23">事業計画の学習・実践・定着</h2>
              <p className="text-46 md:text-31">
                <span className="text-blue">「戦略見える化シート」</span>で
                <br className="md:hidden" />
                事業計画・経営計画を簡単に作成
              </p>
            </div>
            <div className="relative w-o-730 md:w-o-1000 mx-auto">
              <div className="box-grad absolute right-0 bottom-0 bg-gradient-to-r from-blue to-navy"></div>
              <div className="relative md:flex md:items-center">
                <Image
                  loading={"eager"}
                  width="415"
                  height="282"
                  src="/about/images/add01_img01.png"
                  alt="戦略見える化シート 見本"
                  className="w-o-400 mx-auto md:ml-0 md:mr-o-48 mb-o-48 md:mb-0"
                />
                <div className="w-o-635 md:w-o-490 mx-auto md:mx-0 text-white text-28 md:text-18 font-medium tracking-o-60 leading-o-18 pb-o-64 md:py-o-72">
                  <p className="mb-o-28 md:mb-o-18">
                    オンラインで質問に答えていくだけで事業計画や経営計画が出来上がる！
                  </p>
                  <p className="mb-o-28 md:mb-o-18">
                    策定した計画はプレビュー画面で見やすく表示。毎年の経営計画策定に加え、補助金申請、融資計画、新事業計画、創業計画などにも活用できます。
                  </p>
                  <p>
                    さらにプレミアムプランでは策定した計画を自動的に計画書として推敲する機能や、AIが計画の改善点をアドバイスする機能も備わります。
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-o-88 mb-o-60 md:mb-o-88">
            <div>
              <div className="mb-o-80">
                <h2 className="text-58 md:text-41 text-blue text-center tracking-o-60 font-bold">
                  社内の学習・実践・定着
                </h2>
                <p className="text-31 text-center font-bold tracking-o-60">
                  オリジナルマンガでビジネス知識・
                  <br className="md:hidden" />
                  ノウハウを分かりやすく学べる
                </p>
              </div>
              <div className="relative mb-o-108">
                <div className="w-o-651 bg-gray h-o-1186 md:h-o-473 md:w-o-1151 absolute right-0 top-[0.355rem]"></div>
                <div className="md:w-o-1000 md:mx-auto">
                  <h2 className="w-o-504 md:w-o-390 mb-o-24 relative">
                    <picture>
                      <source srcSet="/about/images/sec03_title01_pc.png" />
                      <Image
                        loading={"eager"}
                        width="756"
                        height="107"
                        src="/about/images/sec03_title01.png"
                        alt="経営のモヤモヤが解消！"
                        className="w-full"
                      />
                    </picture>
                  </h2>
                </div>
                <div className="w-o-648 mx-auto relative mb-o-32 md:mb-o-48">
                  <p className="text-46 md:text-31 font-bold leading-o-16">
                    <span className="text-blue">お悩み解決</span>につながる
                    <br className="md:hidden" />
                    講座ランキング
                  </p>
                </div>
                <div className="w-o-689 mx-auto relative md:w-o-1000 md:flex md:items-start md:gap-o-60">
                  <div className="flex items-start justify-between md:w-o-444">
                    <div className="w-o-286 md:w-o-195">
                      <Image
                        loading={"eager"}
                        width="429"
                        height="630"
                        src="/about/images/sec03_book01.jpg"
                        alt="画像のイメージ"
                        className="shadow-[0_3px_16px_rgba(8,41,108,0.3)] w-full"
                      />
                    </div>
                    <div className="w-o-346 md:w-o-213 mb-o-64 md:mb-0">
                      <div className="text-28 md:text-18 font-medium tracking-[0.07em] mb-o-72 md:mb-o-60 leading-o-18">
                        <div className="mb-o-20 md:mb-o-16">
                          <p className="whitespace-nowrap">
                            経営の悩みをなんとかし
                          </p>
                          <p className="whitespace-nowrap">
                            たい時にこそ読みたい！
                          </p>
                        </div>
                        <div>
                          <p className="whitespace-nowrap">
                            中小企業支援の実務をも
                          </p>
                          <p className="whitespace-nowrap">
                            とにした実践的な解決事
                          </p>
                          <p className="whitespace-nowrap">例を揃えました。</p>
                        </div>
                      </div>
                      <Link
                        href="https://app.s-d-lab.jp/tactics/3c3732a9-3cef-464d-851c-85d57c5d3f62"
                        target="_blank"
                        className="w-o-341 md:w-o-213 btn_color rounded-full h-o-80 md:h-o-50 flex items-center justify-center relative mx-auto md:ml-0"
                      >
                        <span className="text-25 md:text-15 font-bold tracking-o-100">
                          無料で読む
                        </span>
                        <span className="block w-o-67 md:w-o-35 absolute right-o-24 md:right-o-12 cta_arrow">
                          <Image
                            loading={"eager"}
                            width="102"
                            height="49"
                            src="/about/images/cta_arrow.png"
                            alt=""
                            className="opacity-0"
                          />
                        </span>
                      </Link>
                    </div>
                  </div>
                  <div className="flex justify-between items-start md:w-o-472">
                    <div className="w-o-217 md:w-o-140">
                      <div className="w-full mb-o-24 md:mb-o-48">
                        <Image
                          loading={"eager"}
                          width="326"
                          height="441"
                          src="/about/images/sec03_book02.jpg"
                          alt="画像のイメージ"
                          className="shadow-[0_3px_16px_rgba(8,41,108,0.3)] w-full"
                        />
                      </div>
                      <Link
                        href="https://app.s-d-lab.jp/tactics/cae11fc7-7d18-468b-85a8-88e0524722b1"
                        target="_blank"
                        className="w-o-217 md:w-o-140 btn_color rounded-full h-o-69 md:h-o-44 flex items-center justify-center relative mx-auto md:ml-0"
                      >
                        <span className="text-21 md:text-13 font-bold tracking-o-100">
                          無料で読む
                        </span>
                        <span className="block w-o-37 md:w-o-23 absolute right-o-8 md:right-o-6 cta_arrow">
                          <Image
                            loading={"eager"}
                            width="102"
                            height="49"
                            src="/about/images/cta_arrow.png"
                            alt=""
                            className="opacity-0"
                          />
                        </span>
                      </Link>
                    </div>
                    <div className="w-o-217 md:w-o-140">
                      <div className="w-full mb-o-24 md:mb-o-48">
                        <Image
                          loading={"eager"}
                          width="326"
                          height="441"
                          src="/about/images/sec03_book03.jpg"
                          alt="画像のイメージ"
                          className="shadow-[0_3px_16px_rgba(8,41,108,0.3)] w-full"
                        />
                      </div>
                      <Link
                        href="https://app.s-d-lab.jp/tactics/3c4b0002-a4f1-4092-a066-2a9a8b40a156"
                        target="_blank"
                        className="w-o-217 md:w-o-140 btn_color rounded-full h-o-69 md:h-o-44 flex items-center justify-center relative mx-auto md:ml-0"
                      >
                        <span className="text-21 md:text-13 font-bold tracking-o-100">
                          無料で読む
                        </span>
                        <span className="block w-o-37 md:w-o-23 absolute right-o-8 md:right-o-6 cta_arrow">
                          <Image
                            loading={"eager"}
                            width="102"
                            height="49"
                            src="/about/images/cta_arrow.png"
                            alt=""
                            className="opacity-0"
                          />
                        </span>
                      </Link>
                    </div>
                    <div className="w-o-217 md:w-o-140">
                      <div className="w-full mb-o-24 md:mb-o-48">
                        <Image
                          loading={"eager"}
                          width="326"
                          height="441"
                          src="/about/images/sec03_book04.jpg"
                          alt="画像のイメージ"
                          className="shadow-[0_3px_16px_rgba(8,41,108,0.3)] w-full"
                        />
                      </div>
                      <Link
                        href="https://app.s-d-lab.jp/tactics/01666933-da59-493f-91be-a5b8c1ba8e16"
                        target="_blank"
                        className="w-o-217 md:w-o-140 btn_color rounded-full h-o-69 md:h-o-44 flex items-center justify-center relative mx-auto md:ml-0"
                      >
                        <span className="text-21 md:text-13 font-bold tracking-o-100">
                          無料で読む
                        </span>
                        <span className="block w-o-37 md:w-o-23 absolute right-o-8 md:right-o-6 cta_arrow">
                          <Image
                            loading={"eager"}
                            width="102"
                            height="49"
                            src="/about/images/cta_arrow.png"
                            alt=""
                            className="opacity-0"
                          />
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative mb-o-108">
                <div className="w-o-651 bg-gray h-o-1186 md:h-o-473 md:w-o-1151 absolute right-0 top-[0.355rem]"></div>
                <div className="md:w-o-1000 md:mx-auto">
                  <h2 className="w-o-571 md:w-o-428 mb-o-24 relative">
                    <picture>
                      <source srcSet="/about/images/sec03_title02_pc.png" />
                      <Image
                        loading={"eager"}
                        width="857"
                        height="107"
                        src="/about/images/sec03_title02.png"
                        alt="経営のモヤモヤが解消！"
                        className="w-full"
                      />
                    </picture>
                  </h2>
                </div>
                <div className="w-o-648 mx-auto relative mb-o-32 md:mb-o-48">
                  <p className="text-46 md:text-31 font-bold leading-o-16">
                    <span className="text-blue">お悩み解決</span>につながる
                    <br className="md:hidden" />
                    講座ランキング
                  </p>
                </div>
                <div className="w-o-689 mx-auto relative md:w-o-1000 md:flex md:items-start md:gap-o-60">
                  <div className="flex items-start justify-between md:w-o-444">
                    <div className="w-o-286 md:w-o-195">
                      <Image
                        loading={"eager"}
                        width="429"
                        height="630"
                        src="/about/images/sec03_book05.jpg"
                        alt="画像のイメージ"
                        className="shadow-[0_3px_16px_rgba(8,41,108,0.3)] w-full"
                      />
                    </div>
                    <div className="w-o-346 md:w-o-213 mb-o-64 md:mb-0">
                      <div className="text-28 md:text-18 font-medium tracking-[0.07em] mb-o-72 md:mb-o-60 leading-o-18">
                        <div className="mb-o-20 md:mb-o-16">
                          <p className="whitespace-nowrap">
                            社内の生産性で悩んでい
                          </p>
                          <p className="whitespace-nowrap">るときはこれ！</p>
                        </div>
                        <div>
                          <p className="whitespace-nowrap">
                            新入社員から管理人材ま
                          </p>
                          <p className="whitespace-nowrap">
                            で、今後の必須とも言え
                          </p>
                          <p className="whitespace-nowrap">
                            るビジネス知識・ノウハ
                          </p>
                          <p className="whitespace-nowrap">
                            ウを習得できます。
                          </p>
                        </div>
                      </div>
                      <Link
                        href="https://app.s-d-lab.jp/tactics/ee4ae1b5-0bec-453a-950c-acafb98a87ef"
                        target="_blank"
                        className="w-o-341 md:w-o-213 btn_color rounded-full h-o-80 md:h-o-50 flex items-center justify-center relative mx-auto md:ml-0"
                      >
                        <span className="text-25 md:text-15 font-bold tracking-o-100">
                          無料で読む
                        </span>
                        <span className="block w-o-67 md:w-o-35 absolute right-o-24 md:right-o-12 cta_arrow">
                          <Image
                            loading={"eager"}
                            width="102"
                            height="49"
                            src="/about/images/cta_arrow.png"
                            alt=""
                            className="opacity-0"
                          />
                        </span>
                      </Link>
                    </div>
                  </div>
                  <div className="flex justify-between items-start md:w-o-472">
                    <div className="w-o-217 md:w-o-140">
                      <div className="w-full mb-o-24 md:mb-o-48">
                        <Image
                          loading={"eager"}
                          width="326"
                          height="441"
                          src="/about/images/sec03_book06.jpg"
                          alt="画像のイメージ"
                          className="shadow-[0_3px_16px_rgba(8,41,108,0.3)] w-full"
                        />
                      </div>
                      <Link
                        href="https://app.s-d-lab.jp/tactics/de490940-b1aa-44b3-97c6-3e1f3e36ea29"
                        target="_blank"
                        className="w-o-217 md:w-o-140 btn_color rounded-full h-o-69 md:h-o-44 flex items-center justify-center relative mx-auto md:ml-0"
                      >
                        <span className="text-21 md:text-13 font-bold tracking-o-100">
                          無料で読む
                        </span>
                        <span className="block w-o-37 md:w-o-23 absolute right-o-8 md:right-o-6 cta_arrow">
                          <Image
                            loading={"eager"}
                            width="102"
                            height="49"
                            src="/about/images/cta_arrow.png"
                            alt=""
                            className="opacity-0"
                          />
                        </span>
                      </Link>
                    </div>
                    <div className="w-o-217 md:w-o-140">
                      <div className="w-full mb-o-24 md:mb-o-48">
                        <Image
                          loading={"eager"}
                          width="326"
                          height="441"
                          src="/about/images/sec03_book07.jpg"
                          alt="画像のイメージ"
                          className="shadow-[0_3px_16px_rgba(8,41,108,0.3)] w-full"
                        />
                      </div>
                      <Link
                        href="https://app.s-d-lab.jp/tactics/3786cf24-e15e-4cbb-af38-70ead77b7d02"
                        target="_blank"
                        className="w-o-217 md:w-o-140 btn_color rounded-full h-o-69 md:h-o-44 flex items-center justify-center relative mx-auto md:ml-0"
                      >
                        <span className="text-21 md:text-13 font-bold tracking-o-100">
                          無料で読む
                        </span>
                        <span className="block w-o-37 md:w-o-23 absolute right-o-8 md:right-o-6 cta_arrow">
                          <Image
                            loading={"eager"}
                            width="102"
                            height="49"
                            src="/about/images/cta_arrow.png"
                            alt=""
                            className="opacity-0"
                          />
                        </span>
                      </Link>
                    </div>
                    <div className="w-o-217 md:w-o-140">
                      <div className="w-full mb-o-24 md:mb-o-48">
                        <Image
                          loading={"eager"}
                          width="326"
                          height="441"
                          src="/about/images/sec03_book08.jpg"
                          alt="画像のイメージ"
                          className="shadow-[0_3px_16px_rgba(8,41,108,0.3)] w-full"
                        />
                      </div>
                      <Link
                        href="https://app.s-d-lab.jp/tactics/dda4fa9a-2285-4930-9549-8109932e09e5"
                        target="_blank"
                        className="w-o-217 md:w-o-140 btn_color rounded-full h-o-69 md:h-o-44 flex items-center justify-center relative mx-auto md:ml-0"
                      >
                        <span className="text-21 md:text-13 font-bold tracking-o-100">
                          無料で読む
                        </span>
                        <span className="block w-o-37 md:w-o-23 absolute right-o-8 md:right-o-6 cta_arrow">
                          <Image
                            loading={"eager"}
                            width="102"
                            height="49"
                            src="/about/images/cta_arrow.png"
                            alt=""
                            className="opacity-0"
                          />
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="w-o-651 bg-gray h-o-1186 md:h-o-473 md:w-o-1151 absolute right-0 top-[0.355rem]"></div>
                <div className="md:w-o-1000 md:mx-auto">
                  <h2 className="w-o-635 md:w-o-497 mb-o-24 relative">
                    <picture>
                      <source srcSet="/about/images/sec03_title03_pc.png" />
                      <Image
                        loading={"eager"}
                        width="714"
                        height="80"
                        src="/about/images/sec03_title03.png"
                        alt="経営のモヤモヤが解消！"
                        className="w-full"
                      />
                    </picture>
                  </h2>
                </div>
                <div className="w-o-648 mx-auto relative mb-o-32 md:mb-o-48">
                  <p className="text-46 md:text-31 font-bold leading-o-16">
                    <span className="text-blue">お悩み解決</span>につながる
                    <br className="md:hidden" />
                    講座ランキング
                  </p>
                </div>
                <div className="w-o-689 mx-auto relative md:w-o-1000 md:flex md:items-start md:gap-o-60">
                  <div className="flex items-start justify-between md:w-o-444">
                    <div className="w-o-286 md:w-o-195">
                      <Image
                        loading={"eager"}
                        width="429"
                        height="630"
                        src="/about/images/sec03_book09.jpg"
                        alt="画像のイメージ"
                        className="shadow-[0_3px_16px_rgba(8,41,108,0.3)] w-full"
                      />
                    </div>
                    <div className="w-o-346 md:w-o-213 mb-o-64 md:mb-0">
                      <div className="text-28 md:text-18 font-medium tracking-[0.07em] mb-o-72 md:mb-o-60 leading-o-18">
                        <div className="mb-o-20 md:mb-o-16">
                          <p className="whitespace-nowrap">
                            毎年変わる制度や法改正
                          </p>
                          <p className="whitespace-nowrap">を専門家が解説！</p>
                        </div>
                        <div>
                          <p className="whitespace-nowrap">
                            中小企業にも対応が求め
                          </p>
                          <p className="whitespace-nowrap">
                            られる制度や法改正など
                          </p>
                          <p className="whitespace-nowrap">
                            について、専門家が分か
                          </p>
                          <p className="whitespace-nowrap">
                            りやすく解説しています
                          </p>
                        </div>
                      </div>
                      <Link
                        href="https://app.s-d-lab.jp/tactics/47d2b6f1-a14c-44c7-b9ad-7e9d0bcef9a6"
                        target="_blank"
                        className="w-o-341 md:w-o-213 btn_color rounded-full h-o-80 md:h-o-50 flex items-center justify-center relative mx-auto md:ml-0"
                      >
                        <span className="text-25 md:text-15 font-bold tracking-o-100">
                          無料で読む
                        </span>
                        <span className="block w-o-67 md:w-o-35 absolute right-o-24 md:right-o-12 cta_arrow">
                          <Image
                            loading={"eager"}
                            width="102"
                            height="49"
                            src="/about/images/cta_arrow.png"
                            alt=""
                            className="opacity-0"
                          />
                        </span>
                      </Link>
                    </div>
                  </div>
                  <div className="flex justify-between items-start md:w-o-472">
                    <div className="w-o-217 md:w-o-140">
                      <div className="w-full mb-o-24 md:mb-o-48">
                        <Image
                          loading={"eager"}
                          width="326"
                          height="441"
                          src="/about/images/sec03_book10.jpg"
                          alt="画像のイメージ"
                          className="shadow-[0_3px_16px_rgba(8,41,108,0.3)] w-full"
                        />
                      </div>
                      <Link
                        href="https://app.s-d-lab.jp/tactics/0dee0fb3-a68e-4d95-b27c-3e4ac2b1aa76"
                        target="_blank"
                        className="w-o-217 md:w-o-140 btn_color rounded-full h-o-69 md:h-o-44 flex items-center justify-center relative mx-auto md:ml-0"
                      >
                        <span className="text-21 md:text-13 font-bold tracking-o-100">
                          無料で読む
                        </span>
                        <span className="block w-o-37 md:w-o-23 absolute right-o-8 md:right-o-6 cta_arrow">
                          <Image
                            loading={"eager"}
                            width="102"
                            height="49"
                            src="/about/images/cta_arrow.png"
                            alt=""
                            className="opacity-0"
                          />
                        </span>
                      </Link>
                    </div>
                    <div className="w-o-217 md:w-o-140">
                      <div className="w-full mb-o-24 md:mb-o-48">
                        <Image
                          loading={"eager"}
                          width="326"
                          height="441"
                          src="/about/images/sec03_book11.jpg"
                          alt="画像のイメージ"
                          className="shadow-[0_3px_16px_rgba(8,41,108,0.3)] w-full"
                        />
                      </div>
                      <Link
                        href="https://app.s-d-lab.jp/tactics/83a9d557-ce98-466c-b433-52212511e8ad"
                        target="_blank"
                        className="w-o-217 md:w-o-140 btn_color rounded-full h-o-69 md:h-o-44 flex items-center justify-center relative mx-auto md:ml-0"
                      >
                        <span className="text-21 md:text-13 font-bold tracking-o-100">
                          無料で読む
                        </span>
                        <span className="block w-o-37 md:w-o-23 absolute right-o-8 md:right-o-6 cta_arrow">
                          <Image
                            loading={"eager"}
                            width="102"
                            height="49"
                            src="/about/images/cta_arrow.png"
                            alt=""
                            className="opacity-0"
                          />
                        </span>
                      </Link>
                    </div>
                    <div className="w-o-217 md:w-o-140">
                      <div className="w-full mb-o-24 md:mb-o-48">
                        <Image
                          loading={"eager"}
                          width="326"
                          height="441"
                          src="/about/images/sec03_book12.jpg"
                          alt="画像のイメージ"
                          className="shadow-[0_3px_16px_rgba(8,41,108,0.3)] w-full"
                        />
                      </div>
                      <Link
                        href="https://app.s-d-lab.jp/tactics/7a1cade6-590b-4760-86d0-40d3ea755a17"
                        target="_blank"
                        className="w-o-217 md:w-o-140 btn_color rounded-full h-o-69 md:h-o-44 flex items-center justify-center relative mx-auto md:ml-0"
                      >
                        <span className="text-21 md:text-13 font-bold tracking-o-100">
                          無料で読む
                        </span>
                        <span className="block w-o-37 md:w-o-23 absolute right-o-8 md:right-o-6 cta_arrow">
                          <Image
                            loading={"eager"}
                            width="102"
                            height="49"
                            src="/about/images/cta_arrow.png"
                            alt=""
                            className="opacity-0"
                          />
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-o-48">
            <div className="relative h-o-912 md:h-o-740">
              <div className="flex items-start justify-center absolute top-0 h-o-912 md:h-o-740 w-full">
                <div className="w-1/2 h-o-912 md:h-o-740 bg-[#F7F7F7]"></div>
                <div className="w-1/2 h-o-912 md:h-o-740 bg-[#EBEEF6]"></div>
                <div className="h-o-765 md:h-o-596 w-full absolute top-0 md:top-o-64 anime-bgmove flex">
                  <Image
                    loading={"eager"}
                    width="1020"
                    height="757"
                    src="/about/images/fv_bg.png"
                    alt=""
                    className="w-full block h-o-765 translate-y-[1.4rem] max-w-none md:hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="1020"
                    height="757"
                    src="/about/images/fv_bg.png"
                    alt=""
                    className="w-full block h-o-765 translate-y-[1.4rem] max-w-none md:hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="1974"
                    height="509"
                    src="/about/images/fv_bg_pc.png"
                    alt=""
                    className="w-full h-o-765 md:h-o-596 translate-y-[1.4rem] md:-translate-y-[0.2rem] max-w-none hidden md:block"
                  />
                  <Image
                    loading={"eager"}
                    width="1974"
                    height="509"
                    src="/about/images/fv_bg_pc.png"
                    alt=""
                    className="w-full h-o-765 md:h-o-596 translate-y-[1.4rem] md:-translate-y-[0.2rem] max-w-none hidden md:block"
                  />
                </div>
              </div>
              <span className="block absolute top-0">
                <picture className="block">
                  <source
                    srcSet="/about/images/cta_top_bg_pc.png"
                    media="(min-width:768px)"
                  />
                  <Image
                    loading={"eager"}
                    src="/about/images/cta_top_bg.png"
                    alt=""
                    className="w-full"
                    width="1148"
                    height="138"
                  />
                </picture>
              </span>
              <span className="block absolute bottom-0">
                <picture className="block">
                  <source
                    srcSet="/about/images/cta_bottom_bg_pc.png"
                    media="(min-width:768px)"
                  />
                  <Image
                    loading={"eager"}
                    width="1148"
                    height="149"
                    src="/about/images/cta_bottom_bg.png"
                    alt=""
                    className="w-full"
                  />
                </picture>
              </span>
              <div className="relative">
                <div className="h-o-184 md:h-o-216 w-[23.39rem] md:w-[20.85rem] absolute top-o-52 md:top-o-64 autoplay-slider">
                  <Image
                    loading={"eager"}
                    width="2339"
                    height="216"
                    src="/about/images/sec01_book.png"
                    alt=""
                    className="max-w-none h-o-216 object-cover block w-full md:hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="2339"
                    height="216"
                    src="/about/images/sec01_book.png"
                    alt=""
                    className="max-w-none h-o-216 object-cover block w-full md:hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="2085"
                    height="184"
                    src="/about/images/sec01_book_pc.png"
                    alt=""
                    className="max-w-none h-o-184 object-cover md:block w-full hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="2085"
                    height="184"
                    src="/about/images/sec01_book_pc.png"
                    alt=""
                    className="max-w-none h-o-184 object-cover md:block w-full hidden"
                  />
                </div>
              </div>
              <div className="pt-o-324 relative md:pt-o-300">
                <p className="tracking-o-60 text-32 md:text-24 text-blue bg-white w-o-252 md:w-o-208 mx-auto text-center font-black py-o-12 md:py-o-8 mb-o-8">
                  戦略ラボなら
                </p>
                <h2 className="text-47 md:text-39 font-black text-center tracking-o-60 mb-o-32 md:mb-o-16">
                  <span className="block">
                    <span className="text-blue tracking-o-60">学び</span>
                    を通じて
                    <span className="text-blue tracking-o-60">
                      自社の生産性
                    </span>
                    を
                  </span>
                  <span className="block">高めることができる</span>
                </h2>
                <Link
                  href="#form"
                  className="w-o-526 md:w-o-317 btn_color h-o-110 md:h-o-72 flex items-center justify-center relative mx-auto"
                >
                  <span className="text-30 md:text-18 font-bold tracking-o-100">
                    まずは無料体験
                  </span>
                  <span className="block w-o-67 md:w-o-39 absolute right-o-40 md:right-o-24 cta_arrow">
                    <Image
                      loading={"eager"}
                      width="102"
                      height="49"
                      src="/about/images/cta_arrow.png"
                      alt=""
                      className="opacity-0"
                    />
                  </span>
                  <div className="w-o-526 md:w-o-317 absolute h-o-110 md:h-o-72 border-[0.02rem] border-white top-o-8 left-o-8"></div>
                </Link>
              </div>
            </div>
          </section>

          <section className="pt-o-72 pb-o-60">
            <div className="mb-o-36">
              <p className="text-46 md:text-31 text-center tracking-o-60 font-bold md:mb-o-4">
                中小企業の戦略ラボ
              </p>
              <picture>
                <source srcSet="/about/images/sec02_title_pc.png" />
                <Image
                  loading={"eager"}
                  width="350"
                  height="120"
                  src="/about/images/sec02_title.png"
                  alt="経営FAQ"
                  className="w-o-348 md:w-o-240 mx-auto -mt-o-16"
                />
              </picture>
            </div>
            <div className="md:w-o-1000 md:mx-auto">
              <div className="w-o-691 ml-auto md:w-full mb-o-48">
                <div className="bg-navy py-o-32">
                  <div className="w-o-613 md:w-o-924 mx-auto flex items-center gap-o-20">
                    <Image
                      loading={"eager"}
                      width="204"
                      height="174"
                      src="/about/images/sec02_q1.png"
                      alt="Q1"
                      className="w-o-136 md:w-o-82"
                    />
                    <div className="text-36 md:text-32 tracking-o-60 font-bold text-white">
                      <p>学習サイトと何が違うの？</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray pb-o-44 md:flex md:items-start md:justify-end md:pt-o-40 md:gap-o-32">
                  <div className="w-o-651 md:w-o-467 ml-auto mb-o-36 md:order-2 md:ml-0 md:mb-0">
                    <picture>
                      <source
                        srcSet="/about/images/sec02_img01_pc.jpg"
                        media="(min-width:768px)"
                      />
                      <Image
                        loading={"eager"}
                        width="651"
                        height="235"
                        src="/about/images/sec02_img01.jpg"
                        alt=""
                        className="w-full pt-o-32 md:pt-0"
                      />
                    </picture>
                  </div>
                  <div className="w-o-613 mx-auto text-28 tracking-o-60 leading-o-18 md:leading-o-20 md:w-o-457 md:text-18 md:order-1 md:mx-0">
                    <p className="mb-o-20">
                      戦略ラボは、中小企業・小規模事業者の皆さまが特に苦手とする経営の「全員学習・社内実践・学び定着」を実現する学習プラットフォームです。
                    </p>
                    <p>
                      実際の経営支援をベースに作られた機能が多く備わり、経営者学習や従業員学習だけでなく、学習内容の実践や学びの定着に向けた機能も多数ご用意しています。一般的な学習サイトと異なり、学びで自社の会社力＝生産性を着実に高めることができます。
                    </p>
                  </div>
                </div>
              </div>
              <div className="w-o-691 ml-auto md:w-full mb-o-48">
                <div className="bg-navy py-o-32">
                  <div className="w-o-613 md:w-o-924 mx-auto flex items-center gap-o-20">
                    <Image
                      loading={"eager"}
                      width="204"
                      height="174"
                      src="/about/images/sec02_q2.png"
                      alt="Q1"
                      className="w-o-136 md:w-o-82"
                    />
                    <div className="text-36 md:text-32 tracking-o-60 font-bold text-white">
                      <p>学びの定着って難しい～</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray pb-o-44 md:flex md:items-start md:justify-end md:pt-o-40 md:gap-o-32">
                  <div className="w-o-651 md:w-o-467 ml-auto mb-o-36 md:order-2 md:ml-0 md:mb-0">
                    <picture>
                      <source
                        srcSet="/about/images/sec02_img02_pc.jpg"
                        media="(min-width:768px)"
                      />
                      <Image
                        loading={"eager"}
                        width="651"
                        height="235"
                        src="/about/images/sec02_img02.jpg"
                        alt=""
                        className="w-full pt-o-32 md:pt-0"
                      />
                    </picture>
                  </div>
                  <div className="w-o-613 mx-auto text-28 tracking-o-60 leading-o-18 md:leading-o-20 md:w-o-457 md:text-18 md:order-1 md:mx-0">
                    <p className="mb-o-20">
                      戦略ラボの各機能はとにかくシンプルな上に、手間なく続けられるよう解説動画や使い方マニュアルが備わっています。
                    </p>
                    <p>
                      有料プランでは従業員無制限の招待機能に加え、研修シートや日報などを自由に作成できる機能や、実践を定着させるための評価シート機能なども備わっています。
                    </p>
                  </div>
                </div>
              </div>
              <div className="w-o-691 ml-auto md:w-full">
                <div className="bg-navy py-o-32">
                  <div className="w-o-613 md:w-o-924 mx-auto flex items-center gap-o-20">
                    <Image
                      loading={"eager"}
                      width="204"
                      height="174"
                      src="/about/images/sec02_q3.png"
                      alt="Q1"
                      className="w-o-136 md:w-o-82"
                    />
                    <div className="text-36 md:text-32 tracking-o-60 font-bold text-white">
                      <p>
                        本当にずっと無料？
                        <br className="md:hidden" />
                        <span className="whitespace-nowrap md:whitespace-normal">
                          有料版メリットも知りたい
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray pb-o-44 md:flex md:items-start md:justify-end md:pt-o-40 md:gap-o-32">
                  <div className="w-o-651 md:w-o-467 ml-auto mb-o-36 md:order-2 md:ml-0 md:mb-0">
                    <picture>
                      <source
                        srcSet="/about/images/sec02_img03_pc.jpg"
                        media="(min-width:768px)"
                      />
                      <Image
                        loading={"eager"}
                        width="651"
                        height="235"
                        src="/about/images/sec02_img03.jpg"
                        alt=""
                        className="w-full pt-o-32 md:pt-0"
                      />
                    </picture>
                  </div>
                  <div className="w-o-613 mx-auto text-28 tracking-o-60 leading-o-18 md:leading-o-20 md:w-o-457 md:text-18 md:order-1 md:mx-0">
                    <p className="mb-o-20">
                      無料版はずっと無料で使えますが、使える機能は戦略見える化シート（作成・プレビューのみ）と学習コンテンツ（1日1コンテンツ）などの一部機能に制限されます。
                    </p>
                    <p>
                      プレミアムプラン（有料版）は従業員の無制限招待が可能となり、学習の実践や定着につなげるための様々な機能を使うことができます。また一部プランでは自動化を促すAI機能も備わっています。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="relative h-o-1012 md:h-o-780">
              <div className="flex items-start justify-center absolute top-0 h-o-912 md:h-o-780 w-full">
                <div className="w-1/2 h-o-1012 md:h-o-780 bg-[#F7F7F7]"></div>
                <div className="w-1/2 h-o-1012 md:h-o-780 bg-[#EBEEF6]"></div>
                <div className="h-o-805 md:h-o-636 w-full absolute top-0 md:top-o-64 anime-bgmove flex">
                  <Image
                    loading={"eager"}
                    width="1020"
                    height="757"
                    src="/about/images/fv_bg.png"
                    alt=""
                    className="w-full block h-o-805 translate-y-[1.4rem] max-w-none md:hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="1020"
                    height="757"
                    src="/about/images/fv_bg.png"
                    alt=""
                    className="w-full block h-o-805 translate-y-[1.4rem] max-w-none md:hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="1974"
                    height="509"
                    src="/about/images/fv_bg_pc.png"
                    alt=""
                    className="w-full h-o-805 md:h-o-636 translate-y-[1.4rem] md:-translate-y-[0.2rem] max-w-none hidden md:block"
                  />
                  <Image
                    loading={"eager"}
                    width="1974"
                    height="509"
                    src="/about/images/fv_bg_pc.png"
                    alt=""
                    className="w-full h-o-805 md:h-o-636 translate-y-[1.4rem] md:-translate-y-[0.2rem] max-w-none hidden md:block"
                  />
                </div>
              </div>
              <span className="block absolute top-0">
                <picture className="block">
                  <source
                    srcSet="/about/images/cta_top_bg_pc.png"
                    media="(min-width:768px)"
                  />
                  <Image
                    loading={"eager"}
                    src="/about/images/cta_top_bg.png"
                    alt=""
                    className="w-full"
                    width="1148"
                    height="138"
                  />
                </picture>
              </span>
              <span className="block absolute bottom-0">
                <picture className="block">
                  <source
                    srcSet="/about/images/cta_bottom_bg_pc.png"
                    media="(min-width:768px)"
                  />
                  <Image
                    loading={"eager"}
                    width="1148"
                    height="149"
                    src="/about/images/cta_bottom_bg.png"
                    alt=""
                    className="w-full"
                  />
                </picture>
              </span>
              <div className="pt-o-220 relative md:pt-o-230">
                <p className="tracking-o-60 text-32 md:text-24 text-blue bg-white w-o-252 md:w-o-208 mx-auto text-center font-black py-o-12 md:py-o-8 mb-o-8">
                  戦略ラボなら
                </p>
                <h2 className="text-47 md:text-39 font-black text-center tracking-o-60 mb-o-48">
                  <span className="block">
                    経営の悩みを「
                    <span className="text-blue tracking-o-60">学び</span>
                    」で
                  </span>
                  <span className="block">解決できる</span>
                </h2>
                <Link
                  href="#form"
                  className="w-o-526 md:w-o-317 btn_color h-o-110 md:h-o-72 flex items-center justify-center relative mx-auto"
                >
                  <span className="text-30 md:text-18 font-bold tracking-o-100">
                    まずは無料体験
                  </span>
                  <span className="block w-o-67 md:w-o-39 absolute right-o-40 md:right-o-24 cta_arrow">
                    <Image
                      loading={"eager"}
                      width="102"
                      height="49"
                      src="/about/images/cta_arrow.png"
                      alt=""
                      className="opacity-0"
                    />
                  </span>
                  <div className="w-o-526 md:w-o-317 absolute h-o-110 md:h-o-72 border-[0.02rem] border-white top-o-8 left-o-8"></div>
                </Link>
              </div>
            </div>
          </section>

          <section className="mt-o-88 mb-o-60 md:mb-o-88">
            <div className="mb-o-80">
              <p className="text-31 text-center font-bold tracking-o-60">
                迷っているあなた！
              </p>
              <h2 className="text-58 md:text-41 text-blue text-center tracking-o-60 font-bold">
                まずは自社の現状を
                <br className="md:hidden" />
                見える化してみましょう
              </h2>
            </div>
            <div>
              <div className="bg-[url(/about/images/sec04_box01_bg.png)] md:bg-[url(/about/images/sec04_box01_bg_pc.png)] bg-cover w-o-730 md:w-o-1000 md:mx-auto pt-o-40 pb-o-80 md:pt-o-36 md:pb-o-72 relative mb-o-100">
                <Image
                  loading={"eager"}
                  src="/about/images/sec04_box01_num.png"
                  width="206"
                  height="231"
                  alt="01"
                  className="absolute w-o-116 h-o-130 right-0 -top-o-64"
                />
                <div className="w-o-613 mx-auto md:w-o-960 md:mr-0">
                  <div className="mb-o-28">
                    <p className="text-21 font-bold tracking-o-60 bg-white rounded-full inline-block px-o-12 text-blue py-o-4 mb-o-8">
                      自社の現状把握に
                    </p>
                    <h3 className="text-41 md:text-31 font-bold text-white tracking-o-60">
                      自社の会社力を見える化するための経営見える化診断
                    </h3>
                  </div>
                  <div className="relative h-o-264 z-10">
                    <Image
                      loading={"eager"}
                      width="1037"
                      height="374"
                      src="/about/images/sec04_box01_img.jpg"
                      alt="イメージ画像"
                      className="absolute object-cover md:right-0 max-w-none w-o-709 md:w-o-691 h-o-249"
                    />
                  </div>
                  <div className="relative h-o-24 w-o-729 md:w-o-960">
                    <div className="h-o-372 md:h-o-164 w-o-729 md:w-o-960 bg-white bg-opacity-20 absolute -top-o-110 md:-top-o-30 -left-o-40 md:left-auto md:right-0"></div>
                  </div>
                  <div className="text-28 md:text-18 md:w-o-878 md:mx-auto font-medium tracking-[0.07em] text-white leading-o-18">
                    <p>
                      経営理念やビジョン、労働環境、業績、ITツールの活用など、多角的観点から経営状況を診断し、改善のヒントを提案します。
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-[url(/about/images/sec04_box02_bg.png)] md:bg-[url(/about/images/sec04_box02_bg_pc.png)] bg-cover w-o-730 md:w-o-1000 md:mx-auto pt-o-40 pb-o-80 md:pt-o-36 md:pb-o-72 relative mb-o-100">
                <Image
                  loading={"eager"}
                  src="/about/images/sec04_box02_num.png"
                  width="206"
                  height="231"
                  alt="01"
                  className="absolute w-o-116 h-o-130 right-0 -top-o-64"
                />
                <div className="w-o-613 mx-auto md:w-o-960 md:mr-0">
                  <div className="mb-o-28">
                    <p className="text-21 font-bold tracking-o-60 bg-white rounded-full inline-block px-o-12 text-blue py-o-4 mb-o-8">
                      どのくらい進んでる？
                    </p>
                    <h3 className="text-41 md:text-31 font-bold text-white tracking-o-60">
                      デジタル化の度合いを測れる
                      <br className="md:hidden" />
                      DX診断
                    </h3>
                  </div>
                  <div className="relative h-o-264 z-10">
                    <Image
                      loading={"eager"}
                      width="1037"
                      height="374"
                      src="/about/images/sec04_box02_img.jpg"
                      alt="イメージ画像"
                      className="absolute object-cover md:right-0 max-w-none w-o-709 md:w-o-691 h-o-249"
                    />
                  </div>
                  <div className="relative h-o-24 w-o-729 md:w-o-960">
                    <div className="h-o-372 md:h-o-164 w-o-729 md:w-o-960 bg-white bg-opacity-20 absolute -top-o-110 md:-top-o-30 -left-o-40 md:left-auto md:right-0"></div>
                  </div>
                  <div className="text-28 md:text-18 md:w-o-878 md:mx-auto font-medium tracking-[0.07em] text-white leading-o-18">
                    <p>
                      基礎力・働き方・効率化・魅力・会計の5つの観点からあなたの会社のDX状況を可視化。弱点把握だけでなく、改善のヒントまで診断します。
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-[url(/about/images/sec04_box03_bg.png)] md:bg-[url(/about/images/sec04_box03_bg_pc.png)] bg-cover w-o-730 md:w-o-1000 md:mx-auto pt-o-40 pb-o-80 md:pt-o-36 md:pb-o-72 relative mb-o-100">
                <Image
                  loading={"eager"}
                  width="219"
                  height="231"
                  src="/about/images/sec04_box03_num.png"
                  alt="02"
                  className="absolute w-o-116 h-o-130 right-0 -top-o-64"
                />
                <div className="w-o-613 mx-auto md:w-o-960 md:mr-0">
                  <div className="mb-o-28">
                    <p className="text-21 font-bold tracking-o-60 bg-white rounded-full inline-block px-o-12 text-blue py-o-4 mb-o-8">
                      目標を設定しましょう
                    </p>
                    <h3 className="text-41 md:text-31 font-bold text-white tracking-o-60">
                      現在の取り組み状況を把握する
                      <br className="md:hidden" />
                      SDGs診断
                    </h3>
                  </div>
                  <div className="relative h-o-264 z-10">
                    <Image
                      loading={"eager"}
                      width="1037"
                      height="374"
                      src="/about/images/sec04_box03_img.jpg"
                      alt="イメージ画像"
                      className="absolute object-cover md:right-0 max-w-none w-o-709 md:w-o-691 h-o-249"
                    />
                  </div>
                  <div className="relative h-o-24 w-o-729 md:w-o-960">
                    <div className="h-o-372 md:h-o-164 w-o-729 md:w-o-960 bg-white bg-opacity-20 absolute -top-o-110 md:-top-o-30 -left-o-40 md:left-auto md:right-0"></div>
                  </div>
                  <div className="text-28 md:text-18 md:w-o-878 md:mx-auto font-medium tracking-[0.07em] text-white leading-o-18">
                    <p>
                      SDGsの考え方は広く浸透し、SDGsに取り組むことで有利になるシーンも増えてきています。具体的な診断項目を活用して、会社のSDGsへの取り組みを総括しましょう。
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-[url(/about/images/sec04_box03_bg.png)] md:bg-[url(/about/images/sec04_box03_bg_pc.png)] bg-cover w-o-730 md:w-o-1000 md:mx-auto pt-o-40 pb-o-80 md:pt-o-36 md:pb-o-72 relative">
                <Image
                  loading={"eager"}
                  width="219"
                  height="231"
                  src="/about/images/sec04_box04_num.png"
                  alt="03"
                  className="absolute w-o-116 h-o-130 right-0 -top-o-64"
                />
                <div className="w-o-613 mx-auto md:w-o-960 md:mr-0">
                  <div className="mb-o-28">
                    <p className="text-21 font-bold tracking-o-60 bg-white rounded-full inline-block px-o-12 text-blue py-o-4 mb-o-8">
                      社員の特徴を把握して
                    </p>
                    <h3 className="text-41 md:text-31 font-bold text-white tracking-o-60">
                      最適な体制づくりにつなげる
                      <br className="md:hidden" />
                      仕事タイプ診断
                    </h3>
                  </div>
                  <div className="relative h-o-264 z-10">
                    <Image
                      loading={"eager"}
                      width="1037"
                      height="374"
                      src="/about/images/sec04_box04_img.jpg"
                      alt="イメージ画像"
                      className="absolute object-cover md:right-0 max-w-none w-o-709 md:w-o-691 h-o-249"
                    />
                  </div>
                  <div className="relative h-o-24 w-o-729 md:w-o-960">
                    <div className="h-o-372 md:h-o-164 w-o-729 md:w-o-960 bg-white bg-opacity-20 absolute -top-o-110 md:-top-o-30 -left-o-40 md:left-auto md:right-0"></div>
                  </div>
                  <div className="text-28 md:text-18 md:w-o-878 md:mx-auto font-medium tracking-[0.07em] text-white leading-o-18">
                    <p>
                      社内でシェアし、各社員の仕事タイプを把握することで、効率的な人員配置や采配が可能に。何回診断しても、もちろん無料です。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-o-48">
            <div className="relative h-o-1012 md:h-o-780">
              <div className="flex items-start justify-center absolute top-0 h-o-912 md:h-o-780 w-full">
                <div className="w-1/2 h-o-1012 md:h-o-780 bg-[#F7F7F7]"></div>
                <div className="w-1/2 h-o-1012 md:h-o-780 bg-[#EBEEF6]"></div>
                <div className="h-o-805 md:h-o-636 w-full absolute top-0 md:top-o-64 anime-bgmove flex">
                  <Image
                    loading={"eager"}
                    width="1020"
                    height="757"
                    src="/about/images/fv_bg.png"
                    alt=""
                    className="w-full block h-o-805 translate-y-[1.4rem] max-w-none md:hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="1020"
                    height="757"
                    src="/about/images/fv_bg.png"
                    alt=""
                    className="w-full block h-o-805 translate-y-[1.4rem] max-w-none md:hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="1974"
                    height="509"
                    src="/about/images/fv_bg_pc.png"
                    alt=""
                    className="w-full h-o-805 md:h-o-636 translate-y-[1.4rem] md:-translate-y-[0.2rem] max-w-none hidden md:block"
                  />
                  <Image
                    loading={"eager"}
                    width="1974"
                    height="509"
                    src="/about/images/fv_bg_pc.png"
                    alt=""
                    className="w-full h-o-805 md:h-o-636 translate-y-[1.4rem] md:-translate-y-[0.2rem] max-w-none hidden md:block"
                  />
                </div>
              </div>
              <span className="block absolute top-0">
                <picture className="block">
                  <source
                    srcSet="/about/images/cta_top_bg_pc.png"
                    media="(min-width:768px)"
                  />
                  <Image
                    loading={"eager"}
                    src="/about/images/cta_top_bg.png"
                    alt=""
                    className="w-full"
                    width="1148"
                    height="138"
                  />
                </picture>
              </span>
              <span className="block absolute bottom-0">
                <picture className="block">
                  <source
                    srcSet="/about/images/cta_bottom_bg_pc.png"
                    media="(min-width:768px)"
                  />
                  <Image
                    loading={"eager"}
                    width="1148"
                    height="149"
                    src="/about/images/cta_bottom_bg.png"
                    alt=""
                    className="w-full"
                  />
                </picture>
              </span>
              <div className="relative">
                <div className="h-o-184 md:h-o-216 w-[23.39rem] md:w-[20.85rem] absolute top-o-52 md:top-o-64 autoplay-slider">
                  <Image
                    loading={"eager"}
                    width="2339"
                    height="216"
                    src="/about/images/sec01_book.png"
                    alt=""
                    className="max-w-none h-o-216 object-cover block w-full md:hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="2339"
                    height="216"
                    src="/about/images/sec01_book.png"
                    alt=""
                    className="max-w-none h-o-216 object-cover block w-full md:hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="2085"
                    height="184"
                    src="/about/images/sec01_book_pc.png"
                    alt=""
                    className="max-w-none h-o-184 object-cover md:block w-full hidden"
                  />
                  <Image
                    loading={"eager"}
                    width="2085"
                    height="184"
                    src="/about/images/sec01_book_pc.png"
                    alt=""
                    className="max-w-none h-o-184 object-cover md:block w-full hidden"
                  />
                </div>
              </div>
              <div className="pt-o-324 relative md:pt-o-300">
                <p className="tracking-o-60 text-32 md:text-24 text-blue bg-white w-o-526 md:w-o-428 mx-auto text-center font-black py-o-12 md:py-o-8 mb-o-8">
                  自社の課題は見つかりましたか？
                </p>
                <h2 className="text-47 md:text-39 font-black text-center tracking-o-60 mb-o-48">
                  <span className="block">
                    経営を「
                    <span className="text-blue">学び</span>
                    」で良くするために
                  </span>
                  <span className="block">
                    今すぐ<span className="text-blue">無料体験を</span>
                  </span>
                </h2>
                <Link
                  href="#form"
                  className="w-o-526 md:w-o-317 btn_color h-o-110 md:h-o-72 flex items-center justify-center relative mx-auto"
                >
                  <span className="text-30 md:text-18 font-bold tracking-o-100">
                    まずは無料体験
                  </span>
                  <span className="block w-o-67 md:w-o-39 absolute right-o-40 md:right-o-24 cta_arrow">
                    <Image
                      loading={"eager"}
                      width="102"
                      height="49"
                      src="/about/images/cta_arrow.png"
                      alt=""
                      className="opacity-0"
                    />
                  </span>
                  <div className="w-o-526 md:w-o-317 absolute h-o-110 md:h-o-72 border-[0.02rem] border-white top-o-8 left-o-8"></div>
                </Link>
              </div>
            </div>
          </section>
          <section>
            <h2 className="text-46 md:text-31 font-bold text-center tracking-o-60 leading-o-12 mb-o-60 md:mb-o-40">
              戦略ラボの無料会員で、
              <br />
              <span className="text-66 md:text-56 text-navy">得られること</span>
            </h2>
            <ul>
              <li className="relative mb-o-20 md:mb-o-28">
                <div className="bg-[#F7F7F7] w-o-730 md:w-o-1114 h-o-226 md:h-o-140 absolute right-0"></div>
                <div className="h-o-226 md:h-o-140 flex items-center gap-o-40 w-o-614 md:w-o-640 mx-auto relative">
                  <Image
                    loading={"eager"}
                    width="138"
                    height="138"
                    src="/about/images/sec05_icon04.png"
                    alt="イメージ画像"
                    className="w-o-92 md:w-o-81 block shadow-[0_3px_16px_rgba(8,41,108,0.3)] rounded-full"
                  />
                  <div>
                    <p className="text-43 font-bold text-blue md:text-25 mb-o-8 whitespace-nowrap">
                      戦略見える化シートで
                      <br className="md:hidden" />
                      経営の方向性を確認
                    </p>
                    <div className="text-26 md:text-18 font-medium">
                      <p>
                        経営の見える化に加え、経営者の学びも深めることができる！
                      </p>
                    </div>
                  </div>
                </div>
              </li>
              <li className="relative mb-o-20 md:mb-o-28">
                <div className="bg-[#EBEEF6] w-o-730 md:w-o-1114 h-o-226 md:h-o-140 absolute left-0"></div>
                <div className="h-o-226 md:h-o-140 flex items-center gap-o-40 w-o-614 md:w-o-640 mx-auto relative">
                  <Image
                    loading={"eager"}
                    width="138"
                    height="138"
                    src="/about/images/sec05_icon01.png"
                    alt="イメージ画像"
                    className="w-o-92 md:w-o-81 block shadow-[0_3px_16px_rgba(8,41,108,0.3)] rounded-full"
                  />
                  <div>
                    <p className="text-43 font-bold text-blue md:text-25 mb-o-8">
                      1日1コンテンツの閲覧
                    </p>
                    <div className="text-26 md:text-18 font-medium">
                      <p>
                        100以上のマンガや経営講座で
                        <br className="md:hidden" />
                        知識を習得することができる！
                      </p>
                    </div>
                  </div>
                </div>
              </li>
              <li className="relative mb-o-20 md:mb-o-28">
                <div className="bg-[#F7F7F7] w-o-730 md:w-o-1114 h-o-226 md:h-o-140 absolute right-0"></div>
                <div className="h-o-226 md:h-o-140 flex items-center gap-o-40 w-o-614 md:w-o-640 mx-auto relative">
                  <Image
                    loading={"eager"}
                    width="138"
                    height="138"
                    src="/about/images/sec05_icon02.png"
                    alt=""
                    className="w-o-92 md:w-o-81 block shadow-[0_3px_16px_rgba(8,41,108,0.3)] rounded-full"
                  />
                  <div>
                    <p className="text-43 font-bold text-blue md:text-25 mb-o-8">
                      経営の最新情報を取得
                    </p>
                    <div className="text-26 md:text-18 font-medium">
                      <p>
                        経営で知っておくべき最新情報を
                        <br className="md:hidden" />
                        リアルタイムで取得できる！
                      </p>
                    </div>
                  </div>
                </div>
              </li>
              <li className="relative mb-o-20 md:mb-o-28">
                <div className="bg-[#EBEEF6] w-o-730 md:w-o-1114 h-o-226 md:h-o-140 absolute left-0"></div>
                <div className="h-o-226 md:h-o-140 flex items-center gap-o-40 w-o-614 md:w-o-640 mx-auto relative">
                  <Image
                    loading={"eager"}
                    width="138"
                    height="138"
                    src="/about/images/sec05_icon03.png"
                    alt="イメージ画像"
                    className="w-o-92 md:w-o-81 block shadow-[0_3px_16px_rgba(8,41,108,0.3)] rounded-full"
                  />
                  <div>
                    <p className="text-43 font-bold text-blue md:text-25 mb-o-8">
                      セミナー・交流会参加
                    </p>
                    <div className="text-26 md:text-18 font-medium">
                      <p>
                        すべてのセミナーと
                        <br className="md:hidden" />
                        一部交流会へ無料でご招待
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </section>
          <section className="py-o-88">
            <Swiper
              modules={[Navigation, Scrollbar, A11y, Keyboard, Autoplay]}
              spaceBetween={5}
              slidesPerView={1}
              navigation
              scrollbar={{ draggable: true }}
              autoplay={true}
            >
              <SwiperSlide>
                <picture className="block">
                  <source
                    srcSet="/about/images/voice_01_pc.png"
                    media="(min-width:768px)"
                  />
                  <Image
                    loading={"eager"}
                    width="662"
                    height="839"
                    src="/about/images/voice_01.png"
                    alt="スライドイメージ"
                    className="w-full"
                  />
                </picture>
              </SwiperSlide>
              <SwiperSlide>
                <picture className="block">
                  <source
                    srcSet="/about/images/voice_02_pc.png"
                    media="(min-width:768px)"
                  />
                  <Image
                    loading={"eager"}
                    width="662"
                    height="949"
                    src="/about/images/voice_02.png"
                    alt="スライドイメージ"
                    className="w-full"
                  />
                </picture>
              </SwiperSlide>
              <SwiperSlide>
                <picture className="block">
                  <source
                    srcSet="/about/images/voice_03_pc.png"
                    media="(min-width:768px)"
                  />
                  <Image
                    loading={"eager"}
                    width="662"
                    height="949"
                    src="/about/images/voice_03.png"
                    alt="スライドイメージ"
                    className="w-full"
                  />
                </picture>
              </SwiperSlide>
              <SwiperSlide>
                <picture className="block">
                  <source
                    srcSet="/about/images/voice_04_pc.png"
                    media="(min-width:768px)"
                  />
                  <Image
                    loading={"eager"}
                    width="662"
                    height="949"
                    src="/about/images/voice_04.png"
                    alt="スライドイメージ"
                    className="w-full"
                  />
                </picture>
              </SwiperSlide>
            </Swiper>
          </section>

          <section className="pt-o-80 relative pb-o-40" id="form">
            <div className="flex items-start justify-center absolute top-0 h-full w-full">
              <div className="w-1/2 h-full bg-[#F7F7F7]"></div>
              <div className="w-1/2 h-full bg-[#EBEEF6]"></div>
              <div className="h-o-402 md:h-o-250 w-full absolute top-0 md:top-o-64 flex">
                <Image
                  loading={"eager"}
                  width="769"
                  height="402"
                  src="/about/images/form_bg.png"
                  alt=""
                  className="w-full block h-o-402 max-w-none md:hidden"
                />
                <Image
                  loading={"eager"}
                  width="769"
                  height="402"
                  src="/about/images/form_bg.png"
                  alt=""
                  className="w-full block h-o-402 max-w-none md:hidden"
                />
                <Image
                  loading={"eager"}
                  width="2306"
                  height="375"
                  src="/about/images/form_bg_pc.png"
                  alt=""
                  className="w-full h-o-765 md:h-o-250 md:-translate-y-[0.2rem] max-w-none hidden md:block"
                />
                <Image
                  loading={"eager"}
                  width="2306"
                  height="375"
                  src="/about/images/form_bg_pc.png"
                  alt=""
                  className="w-full h-o-765 md:h-o-250 md:-translate-y-[0.2rem] max-w-none hidden md:block"
                />
              </div>
            </div>
            <div className="text-47 md:text-39 text-center text-blue font-black mb-o-24 relative">
              <p>学びを身近に</p>
              <p>
                そして仕事をもっと
                <br className="md:hidden" />
                楽しくカンタンに
              </p>
            </div>
            <div className="items-center flex justify-center gap-o-24 mb-o-120 relative">
              <span className="h-o-2 w-o-89 bg-black block"></span>
              <p className="text-26 font-bold">30秒で無料体験に登録！</p>
              <span className="h-o-2 w-o-89 bg-black block"></span>
            </div>
            <form
              className="w-o-691 md:w-o-1228 mx-auto relative mb-o-60"
              onSubmit={handleSubmit((data) => onSubmit(data))}
            >
              <div className="md:flex md:items-center md:w-o-1227 md:mx-auto md:justify-between mb-o-48 md:mb-4 last:mb-0">
                <label className="text-28 md:text-21 font-bold tracking-o-30 mb-o-8 block md:mb-0">
                  会社名・屋号
                </label>
                <input
                  type="text"
                  className="py-o-20 md:py-o-8 px-o-16 md:w-o-901 w-full rounded-[4px] border border-[#707070] text-28 md:text-21 leading-[1]"
                  placeholder="株式会社戦略デザインラボ"
                  required
                  {...register("name_ja", {
                    required: true,
                  })}
                />
              </div>
              <div className="md:flex md:items-center md:w-o-1227 md:mx-auto md:justify-between mb-o-48 md:mb-4 last:mb-0">
                <label className="text-28 md:text-21 font-bold tracking-o-30 mb-o-8 block md:mb-0">
                  メールアドレス
                </label>
                <input
                  type="email"
                  className="py-o-20 md:py-o-8 px-o-16 md:w-o-901 w-full rounded-[4px] border border-[#707070] text-28 md:text-21 leading-[1]"
                  placeholder="sample@lab.com"
                  required
                  {...register("email", {
                    required: true,
                    validate: async (value) => await emailValidate(value),
                  })}
                />
              </div>
              <div className="md:flex md:items-center md:w-o-1227 md:mx-auto md:justify-between mb-o-48 md:mb-4 last:mb-0">
                <label className="text-28 md:text-21 font-bold tracking-o-30 mb-o-8 block md:mb-0">
                  姓
                </label>
                <input
                  type="text"
                  className="py-o-20 md:py-o-8 px-o-16 md:w-o-901 w-full rounded-[4px] border border-[#707070] text-28 md:text-21 leading-[1]"
                  placeholder="山田"
                  required
                  {...register("last_name_ja", { required: true })}
                />
              </div>
              <div className="md:flex md:items-center md:w-o-1227 md:mx-auto md:justify-between mb-o-48 md:mb-4 last:mb-0">
                <label className="text-28 md:text-21 font-bold tracking-o-30 mb-o-8 block md:mb-0">
                  名
                </label>
                <input
                  type="text"
                  className="py-o-20 md:py-o-8 px-o-16 md:w-o-901 w-full rounded-[4px] border border-[#707070] text-28 md:text-21 leading-[1]"
                  placeholder="花子"
                  required
                  {...register("first_name_ja", { required: true })}
                />
              </div>
              <div className="md:flex md:items-center md:w-o-1227 md:mx-auto md:justify-between mb-o-48 md:mb-4 last:mb-0">
                <label className="text-28 md:text-21 font-bold tracking-o-30 mb-o-8 block md:mb-0">
                  ニックネーム
                </label>
                <input
                  type="text"
                  className="py-o-20 md:py-o-8 px-o-16 md:w-o-901 w-full rounded-[4px] border border-[#707070] text-28 md:text-21 leading-[1]"
                  placeholder="はな"
                  required
                  {...register("nickname", {
                    required: true,
                    maxLength: 10,
                  })}
                />
              </div>
              <div className="md:flex md:items-center md:w-o-1227 md:mx-auto md:justify-between mb-o-48 md:mb-4 last:mb-0 flex-wrap">
                <label className="text-28 md:text-21 font-bold tracking-o-30 mb-o-8 block md:mb-0">
                  パスワード
                </label>
                <input
                  type="password"
                  className="py-o-20 md:py-o-8 px-o-16 md:w-o-901 w-full rounded-[4px] border border-[#707070] text-28 md:text-21 leading-[1]"
                  placeholder="8文字以上、英字と数字を含む文字列"
                  autoComplete={"off"}
                  required
                  {...register("password", {
                    required: true,
                    minLength: 8,
                    pattern: /^(?=.*?[a-z])(?=.*?\d)[a-z\d]{8,}$/i,
                  })}
                />
                {errors.password && (
                  <p
                    className="md:w-o-901 w-full ml-auto text-red text-16 md:text-20 pointer-events-none"
                    id="js-errorMessage01"
                  >
                    パスワードは8文字以上で、英字と数字が1文字以上含まれている必要があります。
                  </p>
                )}
              </div>
              <div className="md:flex md:items-center md:w-o-1227 md:mx-auto md:justify-between mb-o-48 md:mb-4 last:mb-0 flex-wrap">
                <label className="text-28 md:text-21 font-bold tracking-o-30 mb-o-8 block md:mb-0">
                  パスワード確認
                </label>
                <input
                  type="password"
                  className="py-o-20 md:py-o-8 px-o-16 md:w-o-901 w-full rounded-[4px] border border-[#707070] text-28 md:text-21 leading-[1]"
                  placeholder=""
                  autoComplete={"off"}
                  required
                  {...register("password_confirmation", {
                    required: true,
                    minLength: 8,
                    pattern: /^(?=.*?[a-z])(?=.*?\d)[a-z\d]{8,}$/i,
                    validate: (value) =>
                      value === password.current ||
                      "The passwords do not match",
                  })}
                />
                {errors.password_confirmation && (
                  <p className="md:w-o-901 w-full ml-auto text-red text-16 md:text-20 pointer-events-none">
                    パスワードが一致しません。
                  </p>
                )}
              </div>
              <div className="md:flex md:items-center md:w-o-1227 md:mx-auto md:justify-between mb-o-48 md:mb-4 last:mb-0">
                <label className="text-28 md:text-21 font-bold tracking-o-30 mb-o-8 block md:mb-0">
                  紹介コード（お持ちの場合）
                </label>
                <input
                  type="text"
                  name="referred_code"
                  className="py-o-20 md:py-o-8 px-o-16 md:w-o-901 w-full rounded-[4px] border border-[#707070] text-28 md:text-21 leading-[1]"
                  placeholder="T123456"
                  required
                />
              </div>
              <p className="relative z-10 text-20 text-center mb-o-4">
                <Link
                  href="https://app.s-d-lab.jp/term"
                  target="_blank"
                  className="underline"
                >
                  利用規約
                </Link>
                ・
                <Link
                  href="https://app.s-d-lab.jp/policy"
                  target="_blank"
                  className="underline"
                >
                  プライバシーポリシー
                </Link>
                に同意
              </p>
              <div className="flex items-center justify-center mb-o-32">
                <input
                  type="checkbox"
                  value="同意する"
                  className="opacity-0 absolute"
                  id="agree"
                  required
                  {...register("policy", {
                    required: true,
                  })}
                />
                <label
                  className="text-28 md:text-20 font-medium checkbox_mark"
                  htmlFor="agree"
                >
                  同意する
                </label>
              </div>
              <button
                type="submit"
                className={`w-o-526 md:w-o-317 btn_color h-o-110 md:h-o-72 flex items-center justify-center relative mx-auto ${
                  loading && "pointer-events-none"
                }`}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin mx-auto h-[20px] w-[20px] text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </>
                ) : (
                  <>
                    <span className="text-30 md:text-18 font-bold tracking-o-100">
                      まずは無料体験
                    </span>
                    <span className="block w-o-67 md:w-o-39 absolute right-o-40 md:right-o-24 cta_arrow">
                      <Image
                        loading={"eager"}
                        src="/about/images/cta_arrow.png"
                        alt=""
                        className="opacity-0"
                        width="102"
                        height="49"
                      />
                    </span>
                    <div className="w-o-526 md:w-o-317 absolute h-o-110 md:h-o-72 border-[0.02rem] border-white top-o-8 left-o-8"></div>
                  </>
                )}
              </button>
            </form>
            <footer className="relative text-20 scale-90 origin-center text-center">
              <p>© 戦略デザインラボ</p>
            </footer>
          </section>
        </div>
      </main>
    </>
  );
};

AboutPage.Layout = NothingLayout;
export default AboutPage;
